import {
  GROUP_NOTE_PREFIX,
  PharmacyEntry,
  buildOptionGroupEntries,
  buildRowTextLines,
  endSentence,
  formatSummaryLine,
} from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/readmission-f2f";
import {
  INITIAL_F2F_OPTION_SECTIONS,
  NOTE_CONTENT_OPTION_SECTIONS,
  NOTE_CONTENT_SECTION_KEYS,
  NOTE_CPT_CODES_ANSWER_KEY,
  NOTE_CPT_FREE_TEXT_ANSWER_KEY,
  NOTE_FREE_TEXT_ANSWER_KEY,
  NOTE_PHARMACIES_ANSWER_KEY,
  NOTE_TRAILING_DOT,
} from "./initial-f2f-constant";
import { CODE_BLOCKS } from "./initial-f2f-code-constant";
import {
  Answers,
  buildGroupAnswers,
  buildRowAnswers,
  getAnswerKey,
  toSection,
} from "./initial-f2f-note-answers";
import {
  CodeItem,
  DateLabelOverrides,
  NoteContentFields,
  NoteSection,
} from "./initial-f2f-types";
import {
  buildCodeLines,
  buildOptionGroupLines,
  buildRowLines,
  entriesToLines,
} from "./initial-f2f-note-lines";
import {
  applyDateLabels,
  buildCodeSummaryLines,
  buildDateSummaryEntries,
  buildDateSummaryLines,
  buildPharmacyLines,
} from "./initial-f2f-utils";

type OptionSectionKey = (typeof NOTE_CONTENT_OPTION_SECTIONS)[number];

export const buildFreeTextSection = (
  freeText: string
): NoteSection | undefined => {
  const text = freeText.trim();
  return text
    ? toSection({ [NOTE_FREE_TEXT_ANSWER_KEY]: { value: text } }, [text])
    : undefined;
};

export const buildPharmacySection = (
  pharmacies: PharmacyEntry[]
): NoteSection | undefined => {
  const named = pharmacies.filter((it) => it.name.trim());
  return toSection(
    named.length
      ? {
          [NOTE_PHARMACIES_ANSWER_KEY]: {
            value: named.map((it) => it.name.trim()),
            pharmacies: named,
          },
        }
      : {},
    buildPharmacyLines(named)
  );
};

export const buildCodeAnswers = (
  codesKey: string,
  freeTextKey: string,
  codes: CodeItem[],
  freeText: string
): Answers => ({
  ...(codes.length && {
    [codesKey]: { value: codes.map((it) => it.code), codes },
  }),
  ...(freeText.trim() && { [freeTextKey]: { value: freeText.trim() } }),
});

export const parseCodes = (answers: Answers, key: string): CodeItem[] =>
  answers[key]?.codes ?? [];

export const parseText = (answers: Answers, key: string): string => {
  const value = answers[key]?.value;
  return typeof value === "string" ? value : "";
};

/** Rows print as dot-terminated sentences, or as one `rowSeparator`-joined line when given. */
export const buildRowSection = (
  labels: string[],
  value: NoteContentFields["hospitalization"],
  freeText: string,
  rowSeparator?: string
): NoteSection | undefined => {
  const note = freeText.trim();
  const lines = [
    ...buildRowTextLines(value),
    ...(note ? [endSentence(`${GROUP_NOTE_PREFIX}${note}`)] : []),
  ];
  const textLines =
    rowSeparator && lines.length
      ? [
          lines
            .map((line) => line.replace(NOTE_TRAILING_DOT, ""))
            .join(rowSeparator),
        ]
      : lines;
  return toSection(
    buildRowAnswers(labels, value, freeText),
    textLines,
    buildRowLines(value, freeText)
  );
};

export const buildOptionSection = (
  key: OptionSectionKey,
  fields: NoteContentFields,
  dateLabels?: DateLabelOverrides
): NoteSection | undefined => {
  const section = INITIAL_F2F_OPTION_SECTIONS[key];
  const store = fields.sectionOptions[key] ?? {};
  const dates = fields.sectionDates[key] ?? {};
  const answers = buildGroupAnswers(section.groups, store);
  Object.entries(dates).forEach(([dateKey, date]) => {
    if (date) answers[getAnswerKey(dateKey)] = { value: date };
  });
  if (section.withCptCodes) {
    Object.assign(
      answers,
      buildCodeAnswers(
        NOTE_CPT_CODES_ANSWER_KEY,
        NOTE_CPT_FREE_TEXT_ANSWER_KEY,
        fields.cptCodes,
        fields.cptFreeText
      )
    );
  }
  const dateGroup = section.dateGroup
    ? applyDateLabels(section.dateGroup, dateLabels)
    : undefined;
  return toSection(
    answers,
    [
      ...(section.withCptCodes
        ? buildCodeSummaryLines(
            CODE_BLOCKS.cpt.summaryLabel,
            fields.cptCodes,
            fields.cptFreeText
          )
        : []),
      ...(dateGroup ? buildDateSummaryLines(dateGroup, dates) : []),
      ...section.groups.flatMap((group) =>
        buildOptionGroupEntries(group, store[group.key]).map(formatSummaryLine)
      ),
    ],
    [
      ...(section.withCptCodes
        ? buildCodeLines(
            CODE_BLOCKS.cpt.summaryLabel,
            fields.cptCodes,
            fields.cptFreeText
          )
        : []),
      ...(dateGroup
        ? entriesToLines(buildDateSummaryEntries(dateGroup, dates))
        : []),
      ...section.groups.flatMap((group) =>
        buildOptionGroupLines(group, store[group.key])
      ),
    ]
  );
};

export const buildOptionSections = (
  fields: NoteContentFields,
  dateLabels?: DateLabelOverrides
): [string, NoteSection | undefined][] =>
  NOTE_CONTENT_OPTION_SECTIONS.map((key) => [
    NOTE_CONTENT_SECTION_KEYS[key],
    buildOptionSection(key, fields, dateLabels),
  ]);
